import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Puzzle, Play, ExternalLink } from "lucide-react";

const STATUS_STYLE = {
  available: "bg-green-100 text-green-700",
  beta: "bg-amber-100 text-amber-700",
  coming_soon: "bg-slate-100 text-slate-500",
};

export default function Plugins() {
  const [plugins, setPlugins] = useState([]);
  const [drafts, setDrafts] = useState({});        // { pluginId: { fieldKey: value } }
  const [ainputs, setAinputs] = useState({});       // { "pid:akey": { inputKey: value } }
  const [aresult, setAresult] = useState({});       // { "pid:akey": { loading, data, error } }

  const load = () =>
    api.get("/plugins").then((r) => {
      setPlugins(r.data);
      const d = {};
      r.data.forEach((p) => (d[p.id] = { ...(p.config || {}) }));
      setDrafts(d);
    });

  useEffect(() => {
    load();
  }, []);

  const toggle = async (p) => {
    try {
      await api.post(`/plugins/${p.id}/toggle`, { enabled: !p.enabled });
      toast.success(`${p.name} ${!p.enabled ? "enabled" : "disabled"}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't update plugin");
    }
  };

  const saveConfig = async (p) => {
    try {
      await api.put(`/plugins/${p.id}/config`, { config: drafts[p.id] || {} });
      toast.success(`${p.name} settings saved`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save settings");
    }
  };

  const setField = (pid, key, val) =>
    setDrafts((d) => ({ ...d, [pid]: { ...d[pid], [key]: val } }));

  const setAField = (pid, akey, ikey, val) =>
    setAinputs((s) => ({ ...s, [`${pid}:${akey}`]: { ...s[`${pid}:${akey}`], [ikey]: val } }));

  const runAction = async (p, a) => {
    const key = `${p.id}:${a.key}`;
    const raw = ainputs[key] || {};
    const payload = {};
    for (const inp of a.inputs) {
      let v = raw[inp.key];
      if (inp.type === "lines") v = String(v || "").split("\n").map((s) => s.trim()).filter(Boolean);
      else if (inp.type === "datetime" && v && v.length === 16) v = v + ":00"; // datetime-local -> RFC3339-ish
      payload[inp.key] = v;
    }
    setAresult((s) => ({ ...s, [key]: { loading: true } }));
    try {
      const res = a.method === "GET"
        ? await api.get(a.endpoint, { params: payload })
        : await api.post(a.endpoint, payload);
      setAresult((s) => ({ ...s, [key]: { data: res.data } }));
      toast.success(`${a.label} — done`);
    } catch (e) {
      const msg = e.response?.data?.detail || "Action failed";
      setAresult((s) => ({ ...s, [key]: { error: msg } }));
      toast.error(msg);
    }
  };

  const renderResult = (data) => {
    if (!data) return null;
    const links = Object.entries(data).filter(
      ([k, v]) => typeof v === "string" && /url|link/i.test(k) && v.startsWith("http")
    );
    return (
      <div className="mt-2 text-xs bg-muted rounded-sm p-2 space-y-1">
        {links.map(([k, v]) => (
          <a key={k} href={v} target="_blank" rel="noreferrer"
            className="flex items-center gap-1 text-ai font-semibold hover:underline">
            <ExternalLink size={12} /> {k.replace(/_/g, " ")}
          </a>
        ))}
        {data.digest && <div className="whitespace-pre-wrap text-slate-700">{data.digest}</div>}
        {"imported" in data && <div>Imported {data.imported}, skipped {data.skipped}.</div>}
        {Array.isArray(data.files) && <div>{data.files.length} file(s): {data.files.slice(0, 5).map((f) => f.name).join(", ")}</div>}
        {Array.isArray(data.contacts) && <div>{data.count} contact(s): {data.contacts.slice(0, 5).map((c) => c.name).join(", ")}</div>}
        {"ok" in data && !links.length && !data.digest && !("imported" in data) && !data.files && !data.contacts && (
          <div>{data.ok ? "✅ Success" : "⚠️ " + (data.omnibox_status || "done")}</div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <Puzzle size={22} />
        <h1 className="text-2xl font-black tracking-tight">Plugins</h1>
      </div>
      <p className="text-sm text-slate-600 mb-6">
        Modular integrations for this workspace. Enable a plugin, fill any config, then use its actions below.
      </p>

      <div className="grid gap-4 md:grid-cols-2 max-w-4xl">
        {plugins.map((p) => (
          <Card key={p.id} className="rounded-sm border-border shadow-none p-5 space-y-3" data-testid={`plugin-card-${p.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-bold">{p.name}</span>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-sm ${STATUS_STYLE[p.status] || ""}`}>
                    {p.status.replace("_", " ")}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5 capitalize">{p.category}</div>
              </div>
              {p.status !== "coming_soon" && (
                <Button size="sm" variant={p.enabled ? "outline" : "default"} className="rounded-sm"
                  onClick={() => toggle(p)} data-testid={`plugin-toggle-${p.id}`}>
                  {p.enabled ? "Disable" : "Enable"}
                </Button>
              )}
            </div>

            <p className="text-sm text-slate-600">{p.description}</p>

            {p.enabled && p.config_fields.length > 0 && (
              <div className="space-y-2 pt-1 border-t border-border">
                {p.config_fields.map((f) => (
                  <div key={f.key}>
                    <label className="label-mono text-slate-500">{f.label}</label>
                    <Input type={f.type === "password" ? "password" : "text"}
                      value={drafts[p.id]?.[f.key] ?? ""}
                      onChange={(e) => setField(p.id, f.key, e.target.value)}
                      className="rounded-sm" data-testid={`plugin-field-${p.id}-${f.key}`} />
                  </div>
                ))}
                <Button size="sm" className="rounded-sm bg-slate-950 hover:bg-slate-800"
                  onClick={() => saveConfig(p)} data-testid={`plugin-save-${p.id}`}>Save</Button>
              </div>
            )}

            {p.enabled && (p.actions || []).length > 0 && (
              <div className="space-y-3 pt-2 border-t border-border">
                <div className="label-mono text-slate-500">Actions</div>
                {p.actions.map((a) => {
                  const rk = `${p.id}:${a.key}`;
                  const r = aresult[rk] || {};
                  return (
                    <div key={a.key} className="space-y-1.5">
                      {a.inputs.map((inp) => (
                        <div key={inp.key}>
                          <label className="label-mono text-slate-500">{inp.label}</label>
                          {inp.type === "textarea" || inp.type === "lines" ? (
                            <Textarea rows={inp.type === "lines" ? 3 : 2} className="rounded-sm text-sm"
                              value={ainputs[rk]?.[inp.key] ?? ""} onChange={(e) => setAField(p.id, a.key, inp.key, e.target.value)}
                              data-testid={`action-${p.id}-${a.key}-${inp.key}`} />
                          ) : inp.type === "select" ? (
                            <select className="w-full text-sm bg-muted rounded-sm px-2 py-1.5 border border-border"
                              value={ainputs[rk]?.[inp.key] ?? inp.options[0]} onChange={(e) => setAField(p.id, a.key, inp.key, e.target.value)}>
                              {inp.options.map((o) => <option key={o} value={o}>{o}</option>)}
                            </select>
                          ) : (
                            <Input type={inp.type === "datetime" ? "datetime-local" : "text"} className="rounded-sm text-sm"
                              value={ainputs[rk]?.[inp.key] ?? ""} onChange={(e) => setAField(p.id, a.key, inp.key, e.target.value)}
                              data-testid={`action-${p.id}-${a.key}-${inp.key}`} />
                          )}
                        </div>
                      ))}
                      <Button size="sm" variant="outline" className="rounded-sm" disabled={r.loading}
                        onClick={() => runAction(p, a)} data-testid={`action-run-${p.id}-${a.key}`}>
                        <Play size={13} className="mr-1.5" /> {r.loading ? "Running…" : a.label}
                      </Button>
                      {r.error && <div className="text-xs text-destructive">{r.error}</div>}
                      {r.data && renderResult(r.data)}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
